import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/util/cn';

// Body type primitive. Inherits font-sans (Inter) from the root.
// Each variant bakes in the size + color + leading combo callers used
// to repeat by hand, so `<Text variant="muted">` ships v3-correct
// without a className override. The `className` prop is for layout
// (margins, max-w, line-clamp, etc) — not for size or color.
//
// Variants:
//   default — body copy. text-base, foreground, relaxed leading.
//   muted   — secondary body. text-base, muted-foreground, relaxed.
//   lead    — intro paragraph. text-lg, muted-foreground, relaxed.
//   small   — supporting copy. text-sm, foreground.
//   subtle  — tertiary detail. text-xs, muted-foreground.
//   eyebrow — uppercase label above a heading. text-xs, bold, wide tracking,
//             muted-foreground. Pair with .text-secondary override for the
//             stronger label variant.

const textVariants = cva(
  'font-sans',
  {
    variants: {
      variant: {
        default: 'text-base leading-relaxed text-foreground',
        muted: 'text-base leading-relaxed text-muted-foreground',
        lead: 'text-lg leading-relaxed text-muted-foreground',
        small: 'text-sm text-foreground',
        subtle: 'text-xs text-muted-foreground',
        eyebrow: 'text-xs font-bold uppercase tracking-wider text-muted-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

type TextElement = 'span' | 'p' | 'div';

export interface TextProps
  extends React.HTMLAttributes<HTMLElement>,
  VariantProps<typeof textVariants> {
  as?: TextElement;
}

const Text = React.forwardRef<HTMLElement, TextProps>(
  ({
    as: Tag = 'p', variant, className, ...props
  }, ref) => (
    <Tag
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any}
      className={cn(textVariants({ variant }), className)}
      {...props}
    />
  ),
);
Text.displayName = 'Text';

export { Text, textVariants };
